import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import TosContent from './TosContent';

interface TosModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const TosModal = ({ isOpen, onAccept, onCancel, loading = false }: TosModalProps) => {
  const [accepted, setAccepted] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (!open && !loading) {
      setAccepted(false);
      onCancel();
    }
  };

  const handleAccept = () => {
    if (accepted && !loading) {
      onAccept();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg bg-adv-charcoal border-adv-darkGray">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">
            Terms of Service
          </DialogTitle>
          <DialogDescription className="text-adv-lightGray">
            Please read and accept our Terms of Service to continue.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh] pr-4">
          <TosContent />
        </ScrollArea>

        <div className="flex items-center space-x-2 pt-2">
          <Checkbox
            id="tos-accept"
            checked={accepted}
            onCheckedChange={(checked) => setAccepted(checked === true)}
            className="data-[state=checked]:bg-adv-orange data-[state=checked]:border-adv-orange"
            disabled={loading}
          />
          <label
            htmlFor="tos-accept"
            className="text-sm text-adv-lightGray cursor-pointer select-none"
          >
            I have read and agree to the Terms of Service
          </label>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setAccepted(false);
              onCancel();
            }}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAccept}
            disabled={!accepted || loading}
            className="btn-primary w-full sm:w-auto"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin mr-2" />
                Processing...
              </>
            ) : (
              'Accept & Continue'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TosModal;
